import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  AccessTime,
  LocationOn,
  LocalShipping,
  Add,
  Delete,
  Edit,
  Save,
  Cancel
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import axiosAPIinstance from '../../config/axiosAPIinstance';

const BusinessConfigManager = () => {
  const [editMode, setEditMode] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axiosAPIinstance.get('/config/getConfig', { secure: true });
        setConfig(transformBackendToFrontend(response.data));
        setLoading(false);
      } catch (err) {
        setError('Failed to load configuration');
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Transform backend DTO to frontend format
  const transformBackendToFrontend = (backendData) => {
    return {
      storeTimings: {
        openingTime: backendData.openingTime || '09:00',
        closingTime: backendData.closingTime || '21:00'
      },
      deliverySettings: {
        radius: backendData.deliveryRadiusKm || 5,
        codEnabled: backendData.codEnabled,
        charges: backendData.deliveryCharges || [
          { minOrder: 0, maxOrder: 250, amount: 35, isFree: false },
          { minOrder: 251, maxOrder: 499, amount: 25, isFree: false },
          { minOrder: 500, maxOrder: null, amount: 0, isFree: true }
        ]
      },
      stores: backendData.storeLocations || [
        {
          id: 1,
          name: 'Main Store',
          latitude: 12.9716,
          longitude: 77.5946,
          isPrimary: true
        }
      ]
    };
  };

  // Transform frontend format to backend DTO
  const transformFrontendToBackend = (frontendData) => {
    return {
      openingTime: frontendData.storeTimings.openingTime,
      closingTime: frontendData.storeTimings.closingTime,
      deliveryRadiusKm: frontendData.deliverySettings.radius,
      codEnabled: frontendData.deliverySettings.codEnabled,
      deliveryCharges: frontendData.deliverySettings.charges,
      storeLocations: frontendData.stores
    };
  };

  // Handle save to backend
  const handleSave = async () => {
    try {
      const backendData = transformFrontendToBackend(config);
      await axiosAPIinstance.put('/config/updateConfig', backendData, { secure: true });
      setEditMode(false);
      setSuccess('Configuration saved successfully');
    } catch (err) {
      setError('Failed to save configuration');
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle nested object changes
  const handleNestedChange = (parent, field, value) => {
    setConfig(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  // Handle delivery charge changes
  const handleDeliveryChargeChange = (index, field, value) => {
    const updatedCharges = [...config.deliverySettings.charges];
    updatedCharges[index] = {
      ...updatedCharges[index],
      [field]: field === 'isFree' ? value : field === 'amount' ? Number(value) : value === '' ? null : Number(value)
    };
    handleNestedChange('deliverySettings', 'charges', updatedCharges);
  };

  const handleStoreChange = (index, field, value) => {
    const updatedStores = [...config.stores];
    const numericFields = ['latitude', 'longitude'];

    updatedStores[index] = {
      ...updatedStores[index],
      [field]: numericFields.includes(field) ? Number(value) : field === 'isPrimary' ? value : value
    };

    setConfig(prev => ({
      ...prev,
      stores: updatedStores
    }));
  };

  // Add new store
  const addNewStore = () => {
    setConfig(prev => ({
      ...prev,
      stores: [
        ...prev.stores,
        {
          id: Date.now(),
          name: '',
          latitude: 0,
          longitude: 0,
          isPrimary: false
        }
      ]
    }));
  };

  // Remove store
  const removeStore = (index) => {
    const updatedStores = config.stores.filter((_, i) => i !== index);
    setConfig(prev => ({
      ...prev,
      stores: updatedStores
    }));
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  // Handle cancel
  const handleCancel = () => {
    axiosAPIinstance.get('/config/getConfig', { secure: true })
      .then(response => {
        setConfig(transformBackendToFrontend(response.data));
        setEditMode(false);
      })
      .catch(err => {
        setError('Failed to reset configuration');
      });
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    return format(parse(time, 'HH:mm', new Date()), 'h:mm a');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Typography color="error">Failed to load configuration</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Card elevation={3} sx={{ p: 3, mb: 3 }}>
        <CardContent sx={{ padding: '0 !important', '&:last-child': { paddingBottom: '0 !important' } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1 }}>
            <Typography variant="h5">Business Configuration</Typography>
            {editMode ? (
              <Box>
                <Button variant="contained" color="primary" startIcon={<Save />} onClick={handleSave} sx={{ mr: 2 }}>
                  Save
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<Cancel />} onClick={handleCancel}>
                  Cancel
                </Button>
              </Box>
            ) : (
              <Button variant="contained" color="primary" startIcon={<Edit />} onClick={toggleEditMode}>
                Edit
              </Button>
            )}
          </Box>

          {/* Store Timings Section */}
          <Box mb={4}>
            <Box display="flex" alignItems="center" mb={2}>
              <AccessTime color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Store Timings</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {editMode ? (
                  <TimePicker
                    label="Opening Time"
                    value={parse(config.storeTimings.openingTime, 'HH:mm', new Date())}
                    onChange={(newValue) => handleNestedChange('storeTimings', 'openingTime', format(newValue, 'HH:mm'))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                ) : (
                  <TextField
                    label="Opening Time"
                    value={formatTimeDisplay(config.storeTimings.openingTime)}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {editMode ? (
                  <TimePicker
                    label="Closing Time"
                    value={parse(config.storeTimings.closingTime, 'HH:mm', new Date())}
                    onChange={(newValue) => handleNestedChange('storeTimings', 'closingTime', format(newValue, 'HH:mm'))}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                ) : (
                  <TextField
                    label="Closing Time"
                    value={formatTimeDisplay(config.storeTimings.closingTime)}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Delivery Settings Section */}
          <Box mb={4}>
            <Box display="flex" alignItems="center" mb={2}>
              <LocalShipping color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Delivery Settings</Typography>
            </Box>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Delivery Radius (KM)"
                  type="number"
                  value={config.deliverySettings.radius}
                  onChange={(e) => handleNestedChange('deliverySettings', 'radius', e.target.value)}
                  fullWidth
                  disabled={!editMode}
                  InputProps={{ endAdornment: <Typography variant="body2">KM</Typography> }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.deliverySettings.codEnabled}
                      onChange={(e) => handleNestedChange('deliverySettings', 'codEnabled', e.target.checked)}
                      disabled={!editMode}
                      color="primary"
                    />
                  }
                  label="Enable Cash on Delivery (COD)"
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" gutterBottom>Delivery Charges</Typography>

            {config.deliverySettings.charges.map((charge, index) => (
              <Grid container spacing={2} key={index} alignItems="center" mb={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Min Order"
                    type="number"
                    value={charge.minOrder}
                    onChange={(e) => handleDeliveryChargeChange(index, 'minOrder', e.target.value)}
                    fullWidth
                    disabled={!editMode}
                    InputProps={{ endAdornment: <Typography variant="body2">₹</Typography> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Max Order"
                    type="number"
                    value={charge.maxOrder || ''}
                    onChange={(e) => handleDeliveryChargeChange(index, 'maxOrder', e.target.value)}
                    fullWidth
                    disabled={!editMode || charge.isFree}
                    placeholder="No limit"
                    InputProps={{ endAdornment: <Typography variant="body2">₹</Typography> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Delivery Charge"
                    type="number"
                    value={charge.amount}
                    onChange={(e) => handleDeliveryChargeChange(index, 'amount', e.target.value)}
                    fullWidth
                    disabled={!editMode || charge.isFree}
                    InputProps={{ endAdornment: <Typography variant="body2">₹</Typography> }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={charge.isFree}
                        onChange={(e) => handleDeliveryChargeChange(index, 'isFree', e.target.checked)}
                        disabled={!editMode}
                        color="primary"
                      />
                    }
                    label="Free Delivery"
                  />
                </Grid>
              </Grid>
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Store Locations Section */}
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <LocationOn color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Store Locations</Typography>
              {editMode && (
                <Button variant="outlined" startIcon={<Add />} onClick={addNewStore} sx={{ ml: 'auto' }}>
                  Add Store
                </Button>
              )}
            </Box>

            {config.stores.map((store, index) => (
              <Box key={store.id} mb={3} p={2} border={1} borderRadius={1} borderColor="divider">
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">{store.name || `Store ${index + 1}`}</Typography>
                  {editMode && (
                    <IconButton color="error" onClick={() => removeStore(index)} disabled={store.isPrimary}>
                      <Delete />
                    </IconButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Store Name"
                      value={store.name}
                      onChange={(e) => handleStoreChange(index, 'name', e.target.value)}
                      fullWidth
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Latitude"
                      type="number"
                      value={store.latitude}
                      onChange={(e) => handleStoreChange(index, 'latitude', e.target.value)}
                      fullWidth
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Longitude"
                      type="number"
                      value={store.longitude}
                      onChange={(e) => handleStoreChange(index, 'longitude', e.target.value)}
                      fullWidth
                      disabled={!editMode}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={store.isPrimary}
                          onChange={(e) => handleStoreChange(index, 'isPrimary', e.target.checked)}
                          disabled={!editMode}
                          color="primary"
                        />
                      }
                      label="Primary Store"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
        <Alert onClose={() => setSuccess(null)} severity="success">{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error">{error}</Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default BusinessConfigManager;